import React, { useEffect, useMemo, useState } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, PaymentElement, useElements, useStripe } from '@stripe/react-stripe-js';
import { X, Check, Loader2, ShieldCheck } from 'lucide-react';
import { useI18n } from '@/i18n/LanguageContext';
import { money } from '@/data/constants';
import { CREDIT_PACKS, CreditPack, packSavings, packUnitPrice } from '@/data/creditPacks';
import { createPackIntent, settlePack } from '@/lib/creditPacksApi';
import { STRIPE_ACCOUNT_ID, STRIPE_PUBLISHABLE_KEY } from '@/lib/singleLead';

/**
 * CREDIT PACK CHECKOUT — 3, 5 or 10 credits in one payment.
 *
 * Uses the SAME Stripe connected account, publishable key and payment gateway
 * as the $29 single-lead purchase that is already live in production. There is
 * no second processor.
 *
 * A pack ONLY adds credits. Nothing is unlocked and nothing is spent here —
 * the contractor spends a credit later, on a project they choose, with Unlock
 * Lead. Settlement is idempotent server-side, so a double tap or a reload
 * cannot double charge or double credit.
 */

const stripePromise = STRIPE_ACCOUNT_ID
  ? loadStripe(STRIPE_PUBLISHABLE_KEY, { stripeAccount: STRIPE_ACCOUNT_ID })
  : null;

interface Props {
  open: boolean;
  onClose: () => void;
  /** Called after credits actually land, with the new spendable balance. */
  onCredited?: (balance: number) => void;
  initialPack?: string;
}

const PayForm: React.FC<{
  pack: CreditPack;
  onDone: (balance: number, granted: number) => void;
}> = ({ pack, onDone }) => {
  const { t, lang } = useI18n();
  const stripe = useStripe();
  const elements = useElements();
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!stripe || !elements || busy) return;
    setBusy(true);
    setErr('');

    const { error, paymentIntent } = await stripe.confirmPayment({
      elements,
      redirect: 'if_required',
    });

    if (error) {
      setErr(error.message || (lang === 'es' ? 'No se pudo procesar el pago.' : 'Payment could not be processed.'));
      setBusy(false);
      return;
    }

    if (paymentIntent?.status === 'succeeded') {
      // Idempotent — retried once on a flaky connection.
      let s = await settlePack(paymentIntent.id, lang);
      if (!s.ok) s = await settlePack(paymentIntent.id, lang);
      setBusy(false);
      if (s.ok) {
        onDone(s.balance, s.creditsGranted);
        return;
      }
      setErr(
        lang === 'es'
          ? 'El pago se realizó. Estamos acreditando su saldo — actualice en un momento.'
          : 'Payment went through. We are crediting your balance — refresh in a moment.'
      );
      return;
    }

    setBusy(false);
    setErr(lang === 'es' ? 'El pago no se completó.' : 'The payment did not complete.');
  };

  return (
    <form onSubmit={submit} className="space-y-4">
      <PaymentElement options={{ layout: 'tabs' }} />
      {err && <p className="rounded-lg bg-red-50 p-3 text-sm font-semibold text-red-700">{err}</p>}
      <button
        type="submit"
        disabled={!stripe || busy}
        style={{ touchAction: 'manipulation' }}
        className="flex w-full items-center justify-center gap-2 rounded-xl bg-orange-500 px-5 py-3.5 text-base font-bold text-white hover:bg-orange-600 disabled:opacity-60"
      >
        {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : null}
        {busy
          ? lang === 'es'
            ? 'Procesando…'
            : 'Processing…'
          : `${lang === 'es' ? 'Pagar' : 'Pay'} ${money(pack.amount)}`}
      </button>
      <p className="flex items-center justify-center gap-1.5 text-[11px] font-semibold text-slate-400">
        <ShieldCheck className="h-3.5 w-3.5" />
        {t('pay.secure')}
      </p>
    </form>
  );
};

const CreditPackModal: React.FC<Props> = ({ open, onClose, onCredited, initialPack }) => {
  const { lang } = useI18n();
  const es = lang === 'es';
  const [selected, setSelected] = useState<string>(initialPack || 'pack5');
  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState<{ balance: number; granted: number } | null>(null);

  const pack = useMemo(() => CREDIT_PACKS.find((p) => p.code === selected) || CREDIT_PACKS[1], [selected]);

  useEffect(() => {
    if (!open) {
      setClientSecret(null);
      setError('');
      setDone(null);
      setLoading(false);
    }
  }, [open]);

  if (!open) return null;

  const startPayment = async () => {
    setLoading(true);
    setError('');
    const intent = await createPackIntent(pack.code, lang);
    setLoading(false);
    if (intent.clientSecret) {
      setClientSecret(intent.clientSecret);
      return;
    }
    setError(
      intent.error === 'payments_not_configured'
        ? es
          ? 'Los pagos se están configurando. Intente de nuevo en unos minutos.'
          : 'Payments are being set up. Please try again shortly.'
        : es
        ? 'No pudimos iniciar el pago. Intente de nuevo.'
        : 'We could not start the payment. Please try again.'
    );
  };

  return (
    <div className="fixed inset-0 z-[70] flex items-end justify-center bg-slate-900/60 p-0 sm:items-center sm:p-4">
      <div className="max-h-[92vh] w-full max-w-lg overflow-y-auto rounded-t-2xl bg-white p-5 shadow-2xl sm:rounded-2xl sm:p-6">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h2 className="text-xl font-extrabold text-slate-900">
              {es ? 'Comprar créditos de lead' : 'Buy lead credits'}
            </h2>
            <p className="mt-1 text-sm text-slate-500">
              {es
                ? 'Los créditos se agregan a su saldo. Se descuentan solo cuando desbloquea un lead.'
                : 'Credits are added to your balance. They are only deducted when you unlock a lead.'}
            </p>
          </div>
          <button
            onClick={onClose}
            aria-label={es ? 'Cerrar' : 'Close'}
            className="rounded-lg p-2 text-slate-400 hover:bg-slate-100 hover:text-slate-700"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {done ? (
          <div className="mt-6 rounded-2xl border border-emerald-200 bg-emerald-50 p-5 text-center">
            <Check className="mx-auto h-10 w-10 text-emerald-600" />
            <p className="mt-3 text-lg font-extrabold text-emerald-900">
              {done.granted > 0
                ? es
                  ? `+${done.granted} créditos agregados`
                  : `+${done.granted} credits added`
                : es
                ? 'Saldo actualizado'
                : 'Balance updated'}
            </p>
            <p className="mt-1 text-sm font-semibold text-emerald-800">
              {es ? 'Saldo disponible' : 'Available balance'}: {done.balance}
            </p>
            <p className="mt-2 text-xs text-emerald-700">
              {es
                ? 'Ningún crédito se gastó en esta compra. Elija cualquier proyecto abierto y presione Desbloquear Lead.'
                : 'No credit was spent on this purchase. Pick any open project and press Unlock Lead.'}
            </p>
            <button
              onClick={() => {
                onCredited?.(done.balance);
                onClose();
              }}
              className="mt-4 w-full rounded-xl bg-slate-900 px-5 py-3 text-sm font-bold text-white hover:bg-slate-800"
            >
              {es ? 'Listo' : 'Done'}
            </button>
          </div>
        ) : clientSecret && stripePromise ? (
          <div className="mt-5">
            <div className="mb-4 flex items-center justify-between rounded-xl bg-slate-50 p-3 text-sm">
              <span className="font-bold text-slate-700">
                {pack.credits} {es ? 'créditos' : 'credits'}
              </span>
              <span className="font-extrabold text-slate-900">{money(pack.amount)}</span>
            </div>
            <Elements stripe={stripePromise} options={{ clientSecret, appearance: { theme: 'stripe' } }}>
              <PayForm pack={pack} onDone={(balance, granted) => setDone({ balance, granted })} />
            </Elements>
            <button
              onClick={() => setClientSecret(null)}
              className="mt-3 w-full rounded-xl border border-slate-200 px-4 py-2.5 text-sm font-bold text-slate-600 hover:bg-slate-50"
            >
              {es ? 'Cambiar paquete' : 'Change pack'}
            </button>
          </div>
        ) : (
          <>
            <div className="mt-5 grid gap-3">
              {CREDIT_PACKS.map((p) => {
                const active = p.code === selected;
                return (
                  <button
                    key={p.code}
                    type="button"
                    onClick={() => setSelected(p.code)}
                    style={{ touchAction: 'manipulation' }}
                    className={`flex items-center justify-between gap-4 rounded-2xl border-2 p-4 text-left transition-colors ${
                      active ? 'border-orange-500 bg-orange-50' : 'border-slate-200 hover:border-slate-300'
                    }`}
                  >
                    <div>
                      <p className="text-base font-extrabold text-slate-900">
                        {p.credits} {es ? 'créditos' : 'credits'}
                        {p.popular && (
                          <span className="ml-2 rounded-full bg-orange-500 px-2 py-0.5 text-[10px] font-bold uppercase text-white">
                            {es ? 'Popular' : 'Popular'}
                          </span>
                        )}
                      </p>
                      <p className="mt-0.5 text-xs font-semibold text-slate-500">
                        {money(packUnitPrice(p))} {es ? 'por lead' : 'per lead'}
                        {packSavings(p) > 0 && (
                          <span className="ml-2 text-emerald-600">
                            {es ? 'ahorra' : 'save'} {money(packSavings(p))}
                          </span>
                        )}
                      </p>
                    </div>
                    <span className="text-lg font-extrabold text-slate-900">{money(p.amount)}</span>
                  </button>
                );
              })}
            </div>

            {error && <p className="mt-4 rounded-lg bg-red-50 p-3 text-sm font-semibold text-red-700">{error}</p>}

            <button
              onClick={startPayment}
              disabled={loading || !stripePromise}
              style={{ touchAction: 'manipulation' }}
              className="mt-5 flex w-full items-center justify-center gap-2 rounded-xl bg-orange-500 px-5 py-3.5 text-base font-bold text-white hover:bg-orange-600 disabled:opacity-60"
            >
              {loading && <Loader2 className="h-5 w-5 animate-spin" />}
              {es ? 'Continuar al pago' : 'Continue to payment'}
            </button>
            {!stripePromise && (
              <p className="mt-3 rounded-lg bg-amber-50 p-3 text-xs font-semibold text-amber-800">
                {es
                  ? 'Los pagos se están configurando.'
                  : 'Payment processing is being set up.'}
              </p>
            )}
            <p className="mt-3 text-center text-[11px] leading-relaxed text-slate-400">
              {es
                ? 'Los créditos comprados no expiran con el ciclo de membresía y nunca se gastan automáticamente.'
                : 'Purchased credits do not expire with the membership cycle and are never spent automatically.'}
            </p>
          </>
        )}
      </div>
    </div>
  );
};

export default CreditPackModal;
